import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { contentAdminApi } from "../../../lib/api";
import { getSchema, PAGE_LABELS } from "./blockSchemas";
import { BlockStatusBadge, FieldStatusDot, relativeTime } from "./BlockStatusBadge";
import { SiteHeader, SiteTabs } from "./SiteDashboard";
import { FieldInput } from "./FieldInput";
import {
  Loader2, Save, Eye, CheckCircle2, RotateCcw, EyeOff, ChevronDown, ChevronUp,
  History, AlertTriangle, Copy, X, Plus, ArrowUp, ArrowDown,
} from "lucide-react";

export default function PageEditor() {
  const { pageKey } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openBlock, setOpenBlock] = useState(null);
  const [historyPanel, setHistoryPanel] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await contentAdminApi.getPage(pageKey);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento pagina");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); setOpenBlock(null); }, [pageKey]);

  const pageTitle = PAGE_LABELS[pageKey] || pageKey;

  const onBlockUpdate = (updated) => {
    setData((prev) => prev ? {
      ...prev,
      blocks: prev.blocks.map((b) => b.id === updated.id ? { ...b, ...updated } : b),
    } : prev);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <SiteTabs activeKey={pageKey} />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Sito Web</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#1F2937]">{pageTitle}</h1>
          {data?.page?.status && (
            <div className="text-xs text-slate-500 mt-2">
              Pagina: {data.page.status} · aggiornata {relativeTime(data.page.updated_at)}
            </div>
          )}
        </div>

        {error && <div className="mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}

        {loading && !data ? (
          <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : data ? (
          <div className="space-y-3">
            {(data.blocks || []).sort((a,b) => (a.display_order||100) - (b.display_order||100)).map((block) => (
              <BlockCard
                key={block.id}
                pageKey={pageKey}
                block={block}
                open={openBlock === block.id}
                onToggle={() => setOpenBlock(openBlock === block.id ? null : block.id)}
                onUpdate={onBlockUpdate}
                onOpenHistory={() => setHistoryPanel(block)}
              />
            ))}
          </div>
        ) : null}

        {historyPanel && (
          <HistoryModal
            block={historyPanel}
            onClose={() => setHistoryPanel(null)}
            onRestore={(updated) => { onBlockUpdate(updated); setHistoryPanel(null); }}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BlockCard
// ---------------------------------------------------------------------------
function BlockCard({ pageKey, block, open, onToggle, onUpdate, onOpenHistory }) {
  const schema = getSchema(pageKey, block.block_key);
  const hasDraft = block.draft_content !== null && block.draft_content !== undefined;
  const state = !block.content || Object.keys(block.content || {}).length === 0
    ? "fallback"
    : hasDraft ? "draft" : "published";

  const [content, setContent] = useState(hasDraft ? block.draft_content : (block.content || {}));
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState(null);
  const [visibilityConfirm, setVisibilityConfirm] = useState(false);

  useEffect(() => {
    setContent(hasDraft ? block.draft_content : (block.content || {}));
    setDirty(false);
  }, [block.id, block.updated_at, hasDraft, block.content, block.draft_content]);

  const setField = (key, value) => {
    setContent((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const saveDraft = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const res = await contentAdminApi.saveDraft(block.id, content, block.updated_at);
      onUpdate(res.data);
      setNotice({ type: "ok", msg: "Bozza salvata." });
      setDirty(false);
    } catch (e) {
      if (e?.response?.status === 409) {
        setNotice({ type: "err", msg: "Il blocco è stato modificato da un altro utente. Ricarica per vedere le modifiche." });
      } else {
        setNotice({ type: "err", msg: e?.response?.data?.detail || "Errore salvataggio bozza" });
      }
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    setNotice(null);
    try {
      // If there are unsaved local changes, save them first as draft
      if (dirty) {
        const dr = await contentAdminApi.saveDraft(block.id, content, block.updated_at);
        onUpdate(dr.data);
      }
      const res = await contentAdminApi.publishBlock(block.id);
      onUpdate(res.data);
      setNotice({ type: "ok", msg: `Blocco pubblicato (v.${res.data.version}).` });
      setDirty(false);
    } catch (e) {
      setNotice({ type: "err", msg: e?.response?.data?.detail || "Errore pubblicazione" });
    } finally {
      setPublishing(false);
    }
  };

  const discardDraft = async () => {
    if (!window.confirm("Sei sicuro di voler eliminare la bozza corrente?")) return;
    try {
      const res = await contentAdminApi.discardDraft(block.id);
      onUpdate(res.data);
      setNotice({ type: "ok", msg: "Bozza annullata." });
    } catch (e) {
      setNotice({ type: "err", msg: e?.response?.data?.detail || "Errore" });
    }
  };

  const previewDraft = async () => {
    try {
      const res = await contentAdminApi.issuePreviewToken([block.id]);
      const url = `${window.location.origin}/${pageKey === "home" ? "" : ({
        about: "chi-siamo", contact: "contatti", join: "diventa-socio",
        events: "eventi", businesses: "attivita",
      }[pageKey] || "")}?preview=${res.data.token}`;
      window.open(url, "_blank");
    } catch (e) {
      setNotice({ type: "err", msg: "Errore generazione anteprima" });
    }
  };

  const toggleVisibility = async () => {
    if (block.is_visible && !visibilityConfirm) {
      setVisibilityConfirm(true);
      setNotice({ type: "warn", msg: "⚠ Se disattivi la visibilità questo blocco sparirà dal sito pubblico. Clicca di nuovo per confermare." });
      setTimeout(() => setVisibilityConfirm(false), 5000);
      return;
    }
    try {
      const res = await contentAdminApi.setVisibility(block.id, !block.is_visible);
      onUpdate(res.data);
      setVisibilityConfirm(false);
      setNotice({ type: "ok", msg: res.data.is_visible ? "Blocco visibile sul sito." : "Blocco nascosto." });
    } catch (e) {
      setNotice({ type: "err", msg: "Errore" });
    }
  };

  return (
    <div className={`bg-white rounded-2xl border ${state === "draft" ? "border-yellow-300" : "border-slate-200"} overflow-hidden`} data-testid={`block-card-${block.block_key}`}>
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
        data-testid={`block-toggle-${block.block_key}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <BlockStatusBadge state={state} is_visible={block.is_visible} />
          <div className="text-left min-w-0">
            <div className="font-semibold text-slate-900 truncate">
              {schema?.label || block.block_key}
              <span className="ml-2 text-xs font-normal text-slate-400">v.{block.version || 1}</span>
            </div>
            <div className="text-xs text-slate-500 truncate">
              {block.block_key} · aggiornato {relativeTime(block.updated_at)}
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-100">
          {/* Sticky action bar */}
          <div className={`sticky top-[68px] z-10 px-4 py-3 border-b flex items-center gap-2 flex-wrap ${state === "draft" ? "bg-yellow-50 border-yellow-200" : "bg-white border-slate-100"}`}>
            {state === "draft" && (
              <div className="text-xs text-yellow-800 font-medium mr-2 inline-flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Bozza in attesa di pubblicazione
              </div>
            )}
            <button
              onClick={saveDraft}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-full px-3 py-1.5 text-sm"
              data-testid={`btn-save-draft-${block.block_key}`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Salva bozza
            </button>
            {hasDraft && (
              <button onClick={previewDraft} className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-slate-400 rounded-full px-3 py-1.5 text-sm" data-testid={`btn-preview-${block.block_key}`}>
                <Eye className="w-3.5 h-3.5" /> Anteprima bozza
              </button>
            )}
            <button
              onClick={publish}
              disabled={publishing || (!hasDraft && !dirty)}
              className="inline-flex items-center gap-1.5 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-40 text-white rounded-full px-3 py-1.5 text-sm"
              data-testid={`btn-publish-${block.block_key}`}
            >
              {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Pubblica
            </button>
            {hasDraft && (
              <button onClick={discardDraft} className="inline-flex items-center gap-1.5 text-slate-500 hover:text-red-600 rounded-full px-3 py-1.5 text-sm" data-testid={`btn-discard-${block.block_key}`}>
                <RotateCcw className="w-3.5 h-3.5" /> Annulla bozza
              </button>
            )}
            <div className="flex-1" />
            <button onClick={onOpenHistory} className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-800 rounded-full px-3 py-1.5 text-sm" data-testid={`btn-history-${block.block_key}`}>
              <History className="w-3.5 h-3.5" /> Storico
            </button>
            <button
              onClick={toggleVisibility}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm ${
                visibilityConfirm ? "bg-red-100 text-red-700" : "text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`btn-visibility-${block.block_key}`}
            >
              {block.is_visible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {block.is_visible ? "Nascondi" : "Mostra"}
            </button>
          </div>

          {/* Notice */}
          {notice && (
            <div className={`mx-4 mt-3 px-3 py-2 text-sm rounded-lg ${
              notice.type === "ok" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
              notice.type === "warn" ? "bg-yellow-50 text-yellow-800 border border-yellow-200" :
              "bg-red-50 text-red-700 border border-red-200"
            }`}>{notice.msg}</div>
          )}

          {/* Body: render fields by type */}
          <div className="p-4">
            {!schema ? (
              <div className="text-sm text-slate-400">Nessuno schema editor definito per <code>{pageKey}.{block.block_key}</code>. Modifica JSON grezzo:
                <textarea className="mt-2 w-full font-mono text-xs border rounded-lg px-2 py-2" rows={10}
                  value={JSON.stringify(content, null, 2)}
                  onChange={(e) => { try { setContent(JSON.parse(e.target.value)); setDirty(true); } catch { /* invalid json */ } }}
                />
              </div>
            ) : schema.type === "list" ? (
              <ListEditor schema={schema} content={content} publishedContent={block.content}
                onChange={(next) => { setContent(next); setDirty(true); }} />
            ) : (
              <FixedEditor schema={schema} content={content} publishedContent={block.content}
                setField={setField} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FixedEditor — flat schema
// ---------------------------------------------------------------------------
function FixedEditor({ schema, content, publishedContent, setField }) {
  return (
    <div className="space-y-4">
      {(schema.fields || []).map((field) => {
        const currentVal = content?.[field.key];
        const publishedVal = publishedContent?.[field.key];
        const state = currentVal === undefined || currentVal === null || currentVal === ""
          ? (publishedVal ? "published" : "fallback")
          : (currentVal !== publishedVal ? "draft" : "published");
        return (
          <div key={field.key}>
            <div className="flex items-center gap-2 mb-1.5">
              <label className="text-sm font-medium text-slate-700">{field.label}</label>
              <FieldStatusDot state={state} />
            </div>
            {field.help && <div className="text-xs text-slate-400 mb-1.5">{field.help}</div>}
            <FieldInput field={field} value={currentVal} onChange={(v) => setField(field.key, v)} />
            {field.placeholders && <div className="text-xs text-slate-400 mt-1">Segnaposto disponibili: {field.placeholders.join(", ")}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ListEditor — header fields + reorderable items
// ---------------------------------------------------------------------------
function ListEditor({ schema, content, publishedContent, onChange }) {
  const setHeaderField = (key, value) => onChange({ ...content, [key]: value });
  const itemsKey = schema.items_field || "items";
  const items = Array.isArray(content?.[itemsKey]) ? content[itemsKey] : [];

  const updateItem = (i, key, value) => {
    const next = items.map((it, idx) => idx === i ? { ...it, [key]: value } : it);
    onChange({ ...content, [itemsKey]: next });
  };
  const removeItem = (i) => {
    onChange({ ...content, [itemsKey]: items.filter((_, idx) => idx !== i) });
  };
  const addItem = () => {
    const blank = {};
    (schema.item_schema || []).forEach((f) => { blank[f.key] = f.type === "boolean" ? true : (f.type === "number" ? 0 : ""); });
    if ("order" in blank || (schema.item_schema || []).some((f) => f.key === "order")) {
      blank.order = (items.length + 1) * 10;
    }
    onChange({ ...content, [itemsKey]: [...items, blank] });
  };
  const move = (i, delta) => {
    const target = i + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[i], next[target]] = [next[target], next[i]];
    onChange({ ...content, [itemsKey]: next });
  };

  return (
    <div className="space-y-6">
      {schema.header_fields?.length > 0 && (
        <div className="space-y-4">
          {schema.header_fields.map((f) => (
            <div key={f.key}>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{f.label}</label>
              <FieldInput field={f} value={content?.[f.key]} onChange={(v) => setHeaderField(f.key, v)} />
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-slate-100 pt-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-slate-800">Elementi ({items.length}{schema.max_items ? ` / ${schema.max_items}` : ""})</h4>
          <button
            onClick={addItem}
            disabled={schema.max_items && items.length >= schema.max_items}
            className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-slate-400 disabled:opacity-40 rounded-full px-3 py-1.5 text-sm"
            data-testid="btn-add-item"
          >
            <Plus className="w-4 h-4" /> Aggiungi
          </button>
        </div>
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="border border-slate-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {i + 1}. {schema.item_label ? schema.item_label(item) : (item.label || item.name || "Elemento")}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30" title="Su">
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => move(i, +1)} disabled={i === items.length - 1} className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30" title="Giù">
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => removeItem(i)} className="p-1 text-slate-400 hover:text-red-600" title="Elimina">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(schema.item_schema || []).map((f) => (
                  <div key={f.key} className={f.type === "longtext" ? "md:col-span-2" : ""}>
                    <label className="text-xs text-slate-500 mb-1 block">{f.label}</label>
                    <FieldInput field={f} value={item[f.key]} onChange={(v) => updateItem(i, f.key, v)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="text-sm text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-xl">
              Nessun elemento. Clicca &quot;Aggiungi&quot; per crearne uno.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HistoryModal
// ---------------------------------------------------------------------------
function HistoryModal({ block, onClose, onRestore }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [restoring, setRestoring] = useState(null);

  const load = async (reset = true) => {
    setLoading(true);
    try {
      const res = await contentAdminApi.blockHistory(block.id, { limit: 20, offset: reset ? 0 : offset });
      setItems(reset ? res.data : [...items, ...res.data]);
      if (reset) setOffset(20); else setOffset(offset + 20);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [block.id]);

  const restore = async (h) => {
    setRestoring(h.id);
    try {
      const res = await contentAdminApi.restoreFromHistory(block.id, h.id);
      onRestore(res.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore ripristino");
    } finally {
      setRestoring(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg">Storico versioni</h3>
            <p className="text-xs text-slate-500 mt-0.5">{block.block_key} — ultime modifiche</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800" data-testid="btn-close-history"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-2">
          {loading && items.length === 0 ? (
            <div className="text-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Nessuna modifica registrata.</div>
          ) : items.map((h) => (
            <div key={h.id} className="border border-slate-200 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between mb-1">
                <div className="font-medium text-slate-800">{h.operation}</div>
                <div className="text-xs text-slate-500">{relativeTime(h.created_at)}</div>
              </div>
              <div className="text-xs text-slate-500 mb-2">{h.user_email || "sistema"}</div>
              <details className="mt-1">
                <summary className="text-xs text-slate-500 cursor-pointer">Vedi campi cambiati</summary>
                <pre className="text-[10px] mt-1 bg-slate-50 p-2 rounded overflow-x-auto">{JSON.stringify(h.changed_fields, null, 2)}</pre>
              </details>
              <button
                onClick={() => restore(h)}
                disabled={restoring === h.id}
                className="mt-2 inline-flex items-center gap-1 text-xs text-[#12964A] hover:underline disabled:opacity-50"
                data-testid={`btn-restore-${h.id}`}
              >
                <RotateCcw className="w-3 h-3" /> Ripristina in bozza
              </button>
            </div>
          ))}
          {items.length > 0 && items.length % 20 === 0 && (
            <button onClick={() => load(false)} className="w-full text-sm text-slate-500 py-2 hover:text-slate-800">Carica altri 20</button>
          )}
        </div>
      </div>
    </div>
  );
}
